<template>
  <v-container class="fill-height" fluid>
    <v-flex :class="{'py-5': $vuetify.breakpoint.smAndDown}">
        <!-- Logo section -->
        <v-layout row justify-center class="mt-5">
          <v-flex xs10>
            <v-layout justify-center>
              <v-img src="../assets/Logo 1.png" max-height="100" max-width="300"></v-img>
            </v-layout>
          </v-flex>
        </v-layout>

        <!-- Redeem Button -->
        <v-layout row justify-center class="mt-5 mb-4">
            <v-btn color="primary" @click="redeemDialog = true" outlined>
                <v-icon left>mdi-ticket-confirmation</v-icon>
                Redeem Kode Buku
            </v-btn>
        </v-layout>

        <v-layout row justify-center class="">
          <v-flex xs12 xl10>
            <!-- Skeleton Loading -->
            <v-layout row justify-center class="" v-if="skeleton">
              <v-flex xs10 md6 lg3 v-for="n in 4" :key="`skeleton-${n}`" :class="{'pa-0 py-6': $vuetify.breakpoint.smAndDown, 'pa-6': $vuetify.breakpoint.mdAndUp}">
                <v-skeleton-loader
                  class="mx-auto"
                  max-width="300"
                  height="300"
                  type="card"
                ></v-skeleton-loader>
              </v-flex>
            </v-layout>

            <!-- Empty State -->
            <v-layout row justify-center v-else-if="filteredMapels.length === 0">
              <v-flex xs10>
                <v-card class="pa-8 text-center">
                  <v-icon size="80" color="grey lighten-2">mdi-book-open-variant</v-icon>
                  <h2 class="headline font-weight-bold grey--text text--darken-1 mt-4">
                    Belum Ada Buku
                  </h2>
                  <p class="subheading grey--text mt-2">
                    Anda belum memiliki buku. Silakan redeem kode unik untuk mengakses buku ujian.
                  </p>
                  <v-btn color="primary" large class="mt-4" @click="redeemDialog = true">
                    <v-icon left>mdi-plus</v-icon>
                    Redeem Kode Pertama
                  </v-btn>
                </v-card>
              </v-flex>
            </v-layout>

            <!-- ✅ FIXED: Use filteredMapels with proper key generation -->
            <v-layout row justify-center class="" v-else>
              <v-flex v-for="(mapel, index) in filteredMapels" 
                :key="generateUniqueKey(mapel, index)" 
                xs12 sm6 md4 lg3 xl3
                :class="{
                  'pa-2 py-4': $vuetify.breakpoint.xs,      
                  'pa-3': $vuetify.breakpoint.smOnly,       
                  'pa-4': $vuetify.breakpoint.mdAndUp       
                }">
                
                <!-- ✅ ENHANCED: Card with expiry overlay -->
                <v-card class="mx-auto book-card position-relative" 
                        min-width="280" max-width="320" 
                        ripple :to="`/paket/${mapel.id}`" hover
                        :class="{ 'expired-book': isExpired(mapel) }"
                        :disabled="isExpired(mapel)">
                  
                  <!-- ✅ NEW: Expiry Status Overlay -->
                  <div class="expiry-overlay" v-if="getExpiryInfo(mapel).show">
                    <v-chip 
                      small 
                      :color="getExpiryInfo(mapel).chipColor" 
                      :text-color="getExpiryInfo(mapel).chipTextColor"
                      class="expiry-chip"
                    >
                      <v-icon small left>{{ getExpiryInfo(mapel).icon }}</v-icon>
                      {{ getExpiryInfo(mapel).text }}
                    </v-chip>
                  </div>

                  <!-- Book Cover -->
                  <v-img :src="mapel.cover" 
                        class="book-cover"
                        width="300" height="433px" 
                        v-if="mapel.cover">
                    <template v-slot:placeholder>
                      <v-row class="fill-height ma-0" align="center" justify="center">
                        <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
                      </v-row>
                    </template>
                    
                    <!-- ✅ NEW: Expired Overlay on Image -->
                    <div v-if="isExpired(mapel)" class="expired-overlay">
                      <v-icon size="60" color="white">mdi-lock</v-icon>
                      <div class="white--text font-weight-bold">KADALUARSA</div>
                    </div>
                  </v-img>
                  
                  <!-- Default Image -->
                  <v-img src="../assets/inggris1.jpeg" 
                        class="book-cover"
                        width="300" height="433px" 
                        v-else>
                    <template v-slot:placeholder>
                      <v-row class="fill-height ma-0" align="center" justify="center">
                        <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
                      </v-row>
                    </template>
                    
                    <!-- ✅ NEW: Expired Overlay on Default Image -->
                    <div v-if="isExpired(mapel)" class="expired-overlay">
                      <v-icon size="60" color="white">mdi-lock</v-icon>
                      <div class="white--text font-weight-bold">KADALUARSA</div>
                    </div>
                  </v-img>

                  <!-- ✅ ENHANCED: Card Content -->
                  <v-card-title class="font-weight-bold justify-center">
                    {{ mapel.nama_mapel }}
                  </v-card-title>
                  
                  <v-card-subtitle class="pt-2">
                    <p class="black--text font-weight-bold">
                      Jenjang {{ mapel.jenjang }}<br>
                      Kelas {{ mapel.grade }}<br>
                      {{ mapel.nomor_produk }}<br>
                    </p>
                    
                    <!-- ✅ NEW: Package count and expiry info -->
                    <div class="d-flex align-center justify-space-between">
                      <v-chip color="primary" text-color="white" small>
                        {{ mapel.paket }} Paket
                      </v-chip>
                    </div>
                    
                    <!-- ✅ NEW: Detailed expiry info -->
                    <div v-if="getExpiryInfo(mapel).showDetail" class="mt-2">
                      <div class="caption" :class="getExpiryInfo(mapel).textClass">
                        <v-icon x-small :color="getExpiryInfo(mapel).iconColor">mdi-calendar-clock</v-icon>
                        {{ getExpiryInfo(mapel).detailText }}
                      </div>
                    </div>
                  </v-card-subtitle>
                </v-card>
              </v-flex>
            </v-layout>
            
            <v-layout row class="lighter-grey--text caption font-weight-thin text-center" justify-center>
              Copyright {{ new Date().getFullYear() }} © Grafindo Media Pratama. All Right Reserved.
            </v-layout>
          </v-flex>
        </v-layout>

        <!-- Redeem Modal -->
        <v-dialog v-model="redeemDialog" persistent max-width="400">
            <v-card>
                <v-card-title class="headline">Verifikasi Kode Buku</v-card-title>
                <v-card-text>
                    <v-text-field
                        label="Masukkan Kode Unik"
                        v-model="kodeUnik"
                        prepend-icon="mdi-key"
                        :loading="redeemLoading"
                        :disabled="redeemLoading"
                        maxlength="9"
                        counter="9"
                        hint="Masukkan 9 digit kode unik dari buku Anda"
                        @keyup.enter="redeemConfirmDialog = true"
                    ></v-text-field>
                    <v-alert type="error" v-if="redeemError">
                        {{ redeemMessage }}
                    </v-alert>
                    <v-alert type="success" v-if="redeemSuccess">
                        {{ redeemMessage }}
                    </v-alert>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="closeRedeemDialog" :disabled="redeemLoading">Batal</v-btn>
                    <v-btn 
                        color="primary" 
                        @click="redeemConfirmDialog = true"
                        :loading="redeemLoading"
                        :disabled="!kodeUnik || kodeUnik.length !== 9 || redeemLoading"
                    >
                        Verifikasi
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="redeemConfirmDialog" max-width="400" persistent>
            <v-card>
                <v-card-title class="headline">Konfirmasi Redeem</v-card-title>
                <v-card-text>Apakah Anda yakin ingin mereedem kode <strong>{{ kodeUnik }}</strong>?</v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="redeemConfirmDialog = false">Batal</v-btn>
                    <v-btn color="primary" text @click="doRedeemCode">Ya, Lanjutkan</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-flex>
  </v-container>
</template>

<script>
import * as api from '@/api'

export default {
  props: {
    source: String,
  },
  data: () => ({
    drawer: false,
    loading: [false, false, false, false],
    login: false,
    mapels: [],
    index: 0,
    nameRules: [
      v => !!v || 'Name is required',
    ],
    emailRules: [
      v => !!v || 'Email is required',
    ],
    errored: false,
    skeleton: false,
    dialog: false,
    emailGuru: '',
    namaGuru: '',
    valid: true,
    
    redeemDialog: false,
    redeemConfirmDialog: false,
    kodeUnik: '',
    redeemLoading: false,
    redeemError: false,
    redeemSuccess: false,
    redeemMessage: '',
    
    dataVersion: 0
  }),
  
  computed: {
    filteredMapels() {
      const now = new Date()
      const mapelMap = new Map()

      this.mapels.forEach((mapel) => {
        const key = mapel.id || `temp-${mapel.nama_mapel}-${mapel.nomor_produk}`
        const isExpired = mapel.exp_date ? now > new Date(mapel.exp_date) : false
        const expiryDate = mapel.exp_date ? new Date(mapel.exp_date) : new Date(0)
        
        if (mapelMap.has(key)) {
          const existing = mapelMap.get(key)
          const existingExpired = existing.exp_date ? now > new Date(existing.exp_date) : false
          const existingExpiryDate = existing.exp_date ? new Date(existing.exp_date) : new Date(0)
          
          let shouldReplace = false
          let reason = ''
          
          if (!isExpired && existingExpired) {
            shouldReplace = true
            reason = 'active_replaces_expired'
          } else if (isExpired && !existingExpired) {
            shouldReplace = false
            reason = 'keep_existing_active'
          } else if (!isExpired && !existingExpired) {
            if (expiryDate > existingExpiryDate) {
              shouldReplace = true
              reason = 'newer_expiry'
            } else {
              shouldReplace = false
              reason = 'keep_existing_newer'
            }
          } else {
            // Both expired - keep latest
            if (expiryDate > existingExpiryDate) {
              shouldReplace = true
              reason = 'latest_expired'
            } else {
              shouldReplace = false
              reason = 'keep_existing_expired'
            }
          }
          
          if (shouldReplace) {
            mapelMap.set(key, { 
              ...mapel, 
              _replaced: true, 
              _replaceReason: reason,
              _previousExpiry: existing.exp_date 
            })
          }
        } else {
          mapelMap.set(key, { ...mapel, _new: true })
        }
      })
      
      // Convert to array and sort (active first, then by name)
      const result = Array.from(mapelMap.values()).sort((a, b) => {
        const aExpired = a.exp_date ? now > new Date(a.exp_date) : false
        const bExpired = b.exp_date ? now > new Date(b.exp_date) : false
        
        if (aExpired !== bExpired) {
          return aExpired ? 1 : -1 // Active first
        }
        
        return a.nama_mapel.localeCompare(b.nama_mapel)
      })

      return result
    }
  },
  
  async mounted(){
    await this.get()
  },
  
  methods:{
    generateUniqueKey(mapel, index) {
      const baseKey = mapel.id || `temp-${index}`
      const statusSuffix = mapel._replaced ? '-replaced' : 
                          mapel._updated ? '-updated' : 
                          mapel._new ? '-new' : ''
      const replaceInfo = mapel._replaceReason ? `-${mapel._replaceReason}` : ''
      return `mapel-${baseKey}-v${this.dataVersion}${statusSuffix}${replaceInfo}`
    },
    
    async get(){
      this.skeleton = true
      
      await api.get('mapel', {}, async (res) => {
        if (res.status) {
          if (res.data && res.data.status) {
            this.dataVersion++
            
            this.mapels = []
            
            await this.$nextTick()
            
            const newMapels = res.data.data || []
            
            this.mapels = newMapels
            
            // newMapels.forEach(mapel => {
            // })
            
          } else {
            this.mapels = []
          }
        } else {
          this.mapels = []
        }
        
        setTimeout(() => {
          this.skeleton = false
        }, 100)
      })
    },
    
    async doRedeemCode() {
      this.redeemConfirmDialog = false
      this.redeemLoading = true
      this.redeemError = false
      this.redeemSuccess = false
      
      await api.post('auth/redeem-unique-code', { kode_unik: this.kodeUnik }, async (res) => {
        if (res.status) {
          if (res.data.status) {
            this.redeemSuccess = true
            this.redeemMessage = res.data.message || 'Kode berhasil di-redeem!'
            this.$store.commit('SHOW_SNACKBAR', { text: this.redeemMessage, color: 'success' })
            
            setTimeout(async () => {
              await this.get() 
              
              if (res.data.replaced) {
                this.redeemMessage += ' (Data akses telah diperbarui)'
              }
              
              if (this.filteredMapels.length > 0) {
                this.redeemMessage += ' Buku berhasil dimuat!'
              }
              
              setTimeout(() => {
                this.closeRedeemDialog()
              }, 2000)
            }, 1000)
            
          } else {
            this.redeemError = true
            this.redeemMessage = res.data.message || 'Kode tidak valid'
            this.$store.commit('SHOW_SNACKBAR', { text: this.redeemMessage, color: 'error' })
          }
        } else {
          this.redeemError = true
          this.redeemMessage = res.data.message || 'Terjadi kesalahan'
          this.$store.commit('SHOW_SNACKBAR', { text: this.redeemMessage, color: 'error' })
        }
        this.redeemLoading = false
      })
    },
    
    isExpired(mapel) {
      if (!mapel.exp_date) return false
      const now = new Date()
      const expiry = new Date(mapel.exp_date)
      return now > expiry
    },
    
  getExpiryInfo(mapel) {
    if (!mapel.exp_date) {
      return { show: false }
    }

    const now = new Date()
    const expiry = new Date(mapel.exp_date)
    const timeDiff = expiry - now
    
    const totalHours = timeDiff / (1000 * 60 * 60)
    const totalDays = timeDiff / (1000 * 60 * 60 * 24)
    
    const hours = Math.floor(totalHours)
    const days = Math.floor(totalDays)
    const remainingHours = Math.floor(totalHours % 24)

    if (timeDiff <= 0) {
      // Expired
      return {
        show: true,
        chipColor: 'error',
        chipTextColor: 'white',
        icon: 'mdi-clock-alert',
        iconColor: 'error',
        text: 'Kadaluarsa',
        shortText: 'Expired',
        textClass: 'error--text',
        showDetail: true,
        detailText: `Berakhir ${this.formatDateShort(expiry)}`
      }
    } else if (hours < 1) {
      const minutes = Math.floor(timeDiff / (1000 * 60))
      return {
        show: true,
        chipColor: 'error',
        chipTextColor: 'white',
        icon: 'mdi-clock-alert',
        iconColor: 'error',
        text: `${minutes} menit lagi`,
        shortText: `${minutes}m`,
        textClass: 'error--text',
        showDetail: true,
        detailText: `Berakhir ${this.formatDateShort(expiry)}`
      }
    } else if (hours < 24) {
      return {
        show: true,
        chipColor: 'error',
        chipTextColor: 'white',
        icon: 'mdi-clock-alert',
        iconColor: 'error',
        text: `${hours} jam lagi`,
        shortText: `${hours}j`,
        textClass: 'error--text',
        showDetail: true,
        detailText: `Berakhir ${this.formatDateShort(expiry)}`
      }
    } else if (hours < 48) {
      const isReallyTomorrow = this.isTomorrow(expiry)
      return {
        show: true,
        chipColor: 'error',
        chipTextColor: 'white',
        icon: 'mdi-clock-alert-outline',
        iconColor: 'error',
        text: isReallyTomorrow ? `Besok (${remainingHours}j)` : `${hours} jam lagi`,
        shortText: isReallyTomorrow ? 'Besok' : `${hours}j`,
        textClass: 'error--text',
        showDetail: true,
        detailText: `Berakhir ${this.formatDateShort(expiry)}`
      }
    } else if (days <= 3) {
      return {
        show: true,
        chipColor: 'warning',
        chipTextColor: 'white',
        icon: 'mdi-clock-alert-outline',
        iconColor: 'warning',
        text: `${days} hari ${remainingHours}j`,
        shortText: `${days}h ${remainingHours}j`,
        textClass: 'warning--text',
        showDetail: true,
        detailText: `Berakhir ${this.formatDateShort(expiry)}`
      }
    } else if (days <= 7) {
      return {
        show: true,
        chipColor: 'orange',
        chipTextColor: 'white',
        icon: 'mdi-clock-outline',
        iconColor: 'orange',
        text: `${days} hari lagi`,
        shortText: `${days}h`,
        textClass: 'orange--text',
        showDetail: false,
        detailText: ''
      }
    } else if (days <= 30) {
      return {
        show: true,
        chipColor: 'blue',
        chipTextColor: 'white',
        icon: 'mdi-information-outline',
        iconColor: 'blue',
        text: `${days} hari lagi`,
        shortText: `${days}h`,
        textClass: 'blue--text',
        showDetail: false,
        detailText: ''
      }
    } else {
      return {
        show: false
      }
    }
  },

  isTomorrow(date) {
    return this.calendarDaysUntil(date) === 1
  },

  /**
   * Selisih hari KALENDER (00:00 hari ini -> 00:00 tanggal target)
   * menurut zona Asia/Jakarta. Tidak dipengaruhi timezone device.
   */
  calendarDaysUntil(date) {
    const dayIndex = (d) => {
      try {
        const parts = new Intl.DateTimeFormat('en-CA', {
          timeZone: 'Asia/Jakarta',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        }).formatToParts(d)
        const get = (type) => parseInt(parts.find(p => p.type === type).value, 10)
        return Date.UTC(get('year'), get('month') - 1, get('day'))
      } catch (e) {
        const wib = new Date(d.getTime() + 7 * 60 * 60 * 1000)
        return Date.UTC(wib.getUTCFullYear(), wib.getUTCMonth(), wib.getUTCDate())
      }
    }
    return Math.round((dayIndex(date) - dayIndex(new Date())) / (1000 * 60 * 60 * 24))
  },

  /** Jam:menit zona Asia/Jakarta tanpa AM/PM (mis. "17.00") */
  formatTimeOnly(date) {
    try {
      return new Intl.DateTimeFormat('id-ID', {
        timeZone: 'Asia/Jakarta',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23'
      }).format(date)
    } catch (e) {
      const wib = new Date(date.getTime() + 7 * 60 * 60 * 1000)
      const hh = String(wib.getUTCHours()).padStart(2, '0')
      const mm = String(wib.getUTCMinutes()).padStart(2, '0')
      return `${hh}.${mm}`
    }
  },

  formatDateShort(date) {
    const diff = this.calendarDaysUntil(date)

    if (diff === 0) {
      return `hari ini ${this.formatTimeOnly(date)}`
    } else if (diff === 1) {
      return `besok ${this.formatTimeOnly(date)}`
    } else {
      return new Intl.DateTimeFormat('id-ID', {
        timeZone: 'Asia/Jakarta',
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }).format(date)
    }
  },
    
    openDialog(index){
      this.index = index
      this.dialog = true
    },
    
    closeRedeemDialog() {
      this.redeemDialog = false
      this.redeemConfirmDialog = false
      this.kodeUnik = ''
      this.redeemError = false
      this.redeemSuccess = false
      this.redeemMessage = ''
    },
  },
}
</script>

<style scoped>
.book-cover {
  object-fit: cover;
  object-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.book-cover >>> .v-image__image {
  background-position: center center !important;
  background-size: cover !important;
}

.book-cover >>> .v-responsive__content {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.book-card {
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
  position: relative;
}

.book-card:hover:not(.expired-book) {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0,0,0,0.15);
}

/* ✅ NEW: Expired book styling */
.expired-book {
  opacity: 0.7;
  filter: grayscale(0.3);
  cursor: not-allowed;
}

.expired-book:hover {
  transform: translateY(-2px); /* Less hover effect for expired */
}

/* ✅ NEW: Expiry overlay on top corner */
.expiry-overlay {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
}

.expiry-chip {
  font-weight: bold;
  box-shadow: 0 2px 8px rgba(0,0,0,0.3);
}

/* ✅ NEW: Expired overlay on image */
.expired-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.6);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1;
}

.v-image {
  margin: 0 auto;
}

/* ✅ NEW: Position relative for overlay */
.position-relative {
  position: relative;
}

/* ✅ NEW: Animation for critical warning */
@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
}

.book-card .v-chip.error {
  animation: pulse 2s infinite;
}

/* ✅ NEW: Card disabled state for expired books */
.book-card[disabled] {
  pointer-events: none;
}
</style>